import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as xml2js from 'xml2js';

import { PlessHttp } from './pless-http.service';
import { PlessService } from '../pless-service.model';
import { PlessDialogService } from '../pless-dialog-service';
import { PlessViewUtils } from '../utils/pless-view-utils.service';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

interface ServiceInfo {
    url: string;
    method: string;
    contentType: string;
    showBusyCursor: boolean;
}

/**
 * APIと通信する際に使用する共通クラス
 * APIと通信するサービスは基本的にこのクラスを継承する
 */
@Injectable()
export class PlessResponder {

    constructor(
        protected plessHttp: PlessHttp,
        protected dialogService: PlessDialogService
    ) { }

    SYSTEM_ERROR_MESSAGE = 'システムエラーが発生しました。\nシステム管理者までご連絡ください。';

    protected serviceInfo: ServiceInfo;

    /**
     * リクエスト送信
     *
     * @param id PlessServiceで指定したAPIのID
     * @param body 送信する内容
     */
    send(id: string, body: any) {
        this.serviceInfo = PlessService.SERVICE[id];
        if (this.serviceInfo == null) {
            console.log('指定されたAPIのIDはPlessServiceに存在しません');
            return;
        }

        let response: Observable<HttpResponse<string>>;

        const csrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
        const jwt = document.cookie.split('; ').find(row => row.startsWith('PEACE_JWT='));

        if (this.serviceInfo.method === 'POST') {
            response = this.plessHttp.post<string>(this.serviceInfo.url, body, {
                headers: new HttpHeaders({
                    'Content-Type': this.serviceInfo.contentType,
                    'X-XSRF-TOKEN': csrfToken ? csrfToken.split('=')[1] : '',
                    Authorization: jwt ? 'Bearer ' + jwt.split('=')[1] : '',
                }), responseType: 'text',
                withCredentials: true
            });
        } else if (this.serviceInfo.method === 'GET') {
            let param = '';
            if (body != null) {
                param = '?' + body;
            }
            response = this.plessHttp.get<string>(this.serviceInfo.url + param, {
                headers: new HttpHeaders({
                    'Content-Type': this.serviceInfo.contentType,
                    'X-XSRF-TOKEN': csrfToken ? csrfToken.split('=')[1] : '',
                    Authorization: jwt ? 'Bearer ' + jwt.split('=')[1] : '',
                }), responseType: 'text',
                withCredentials: true
            });
        }

        this.result(response);
    }

    /**
     * 送信処理後の処理
     *
     * @param httpresponse
     */
    protected result(httpresponse: Observable<HttpResponse<string>>) {

        if (this.serviceInfo.showBusyCursor) {
            this.plessHttp.createBlockLayer();
        }

        if (httpresponse == null) {
            this.fault();
        }

        httpresponse.subscribe(
            response => {
                if (this.serviceInfo.showBusyCursor) {
                    this.plessHttp.removeBlockLayer();
                }
                // リクエスト成功時の処理
                // 受け取ったXMLをJavaScriptObjectに変換する
                let rsp;
                xml2js.parseString(response.body, function (err, result) {
                    rsp = result.pless.rsp[0];
                });

                let status = '';
                if (rsp == null) {
                    this.fault();
                    return;
                } else {
                    status = rsp.status[0];
                    status = status.toLocaleLowerCase();
                }

                if (status === 'ok') {
                    if (rsp.data == null) {
                        this.success(null);
                    } else {
                        this.success(rsp.data[0]);
                    }
                } else {
                    this.statusError(status, rsp);
                }
            },
            error => {
                if (this.serviceInfo.showBusyCursor) {
                    this.plessHttp.removeBlockLayer();
                }
                // リクエスト失敗時の処理
                this.fault();
            }
        );
    }

    /**
     * StatusがOK以外の場合の処理
     * 独自のステータス処理を実装したい場合はこのメソッドをオーバーライドする
     *
     * @param status statauタグの値
     * @param result レスポンスの内容(rspタグの値)
     */
    protected statusError(status: string, result: any) {
        if (status === 'unauthorized') {
            // "認証エラー"時の処理
            this.unauthorizedError(result);
        } else if (status === 'forbidden') {
            // "権限エラー"時の処理
            this.forbiddenError(result);
        } else if (status === 'validationerror') {
            // "検証エラー"時の処理
            this.validationError(result);
        } else if (status === 'parametererror') {
            // "パラメータエラー"時の処理
            this.parameterError(result);
        } else {
            this.exceptionError(status, result);
        }
    }

    /**
     * 認証エラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected unauthorizedError(result: any) {
        this.showErrorMessage(result.message[0], '認証エラー');
    }

    /**
     * 権限エラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected forbiddenError(result: any) {
        this.showErrorMessage(result.message[0], '権限エラー');
    }

    /**
     * 検証エラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected validationError(result: any) {
        this.showErrorMessage(this.makeErrMsg(result.data[0]), '検証エラー');
    }

    /**
     * パラメータエラーが帰ってきた場合に呼び出されるメソッド。
     */
    protected parameterError(result: any) {
        this.showErrorMessage(this.makeErrMsg(result.data[0]), 'パラメータエラー');
    }

    /**
     * 特殊なエラーが返ってきた場合に呼び出されるメソッド。<br/>
     * 機能独自のステータスコードを持つ場合、このメソッドをオーバーライドして処理を実装する。
     */
    exceptionError(status: string, result: any) {
        let errMsg: string;
        if (status === 'systemerror') {
            errMsg = this.SYSTEM_ERROR_MESSAGE;
        } else {
            errMsg = result.message[0];
        }
        this.showErrorMessage(errMsg, 'エラー発生');
    }

    /**
     * リクエスト成功時のメソッド。
     * 成功時の処理を実装する場合、このメソッドをオーバーライドして処理を実装する。
     *
     * @param data dataタグの値
     */
    success(data: any) {
    }

    /**
     * リクエスト失敗時のメソッド。
     * 失敗時の処理を実装する場合、このメソッドをオーバーライドして処理を実装する。
     */
    fault() {
        this.showErrorMessage('サーバへの接続に失敗しました。', 'エラー発生');
    }

    /**
     * バリデーションエラーメッセージをまとめた文字列を作成
     */
    private makeErrMsg(errList: any): string {
        let errMsg = '';
        if (errList.detail != null) {
            const detail = errList.detail;
            for (let i = 0; i < detail.length; i++) {
                errMsg += detail[i].code[0] + ' ' + detail[i].msg[0] + '\n';
            }
        }
        return errMsg;
    }

    /**
     * 各エラーの発生時にエラーメッセージを表示するためのメソッド<br>
     * オーバーライドによる動作の変更が可能
     */
    protected showErrorMessage(message: string, title: string) {
        this.dialogService.openAlert(message, 'E');
    }

    /**
     * 特殊なメッセージ（チェック結果など）をまとめた文字列を作成
     */
    makeParticularMsg(msgList: any): string {
        let message = '';
        if (msgList != null) {
            for (let i = 0; i < msgList.length; i++) {
                if (msgList[i] == null) {
                    message += '\n';
                } else {
                    message += msgList[i].toString() + '\n';
                }
            }
        }
        return message;
    }

    /**
     * XMLのタグ内で使用できない文字をエスケープする
     *
     * @param value エスケープ対象文字列
     */
    protected escapeXML(value: string): string {
        return PlessViewUtils.escapeXML(value);
    }
}
